import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
} from "@tanstack/react-router";
import {
	BotMessageSquareIcon,
	ChevronLeft,
	Menu,
	Settings2Icon,
	UserIcon,
} from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { currentPageContextAtom } from "~/components/practice/chat-widget";
import { generateSettingsPageContext } from "~/lib/context-gen";
import { m } from "~/i18n/messages";

export const Route = createFileRoute("/app/settings")({
	component: SettingsPage,
});

type SettingsNavLinkProps = {
	href: string;
	icon: React.ElementType;
	label: string;
	onClick?: () => void;
};

function SettingsNavLink(props: SettingsNavLinkProps) {
	const location = useLocation();
	const isActive = location.pathname.startsWith(props.href);

	return (
		<Link to={props.href} preload="intent">
			<Button
				onClick={props.onClick}
				className={cn(
					"w-full justify-start transition-none gap-2",
					isActive &&
						"text-white bg-pink-400 font-semibold hover:text-white hover:bg-pink-500",
				)}
				variant={isActive ? "default" : "ghost"}
			>
				<props.icon className="text-inherit size-4" />
				{props.label}
			</Button>
		</Link>
	);
}

function SettingsPage() {
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);

	const setPageContext = useSetAtom(currentPageContextAtom);

	useEffect(() => {
		setPageContext({
			title: m["settings.title"](),
			slug: "/app/settings",
			contextData: generateSettingsPageContext({}),
		});
	}, [setPageContext]);

	return (
		<div className="lg:flex h-full w-full">
			{/* Mobile Sidebar Toggle */}
			<Button
				variant="ghost"
				size="icon"
				onClick={() => setIsSidebarOpen(!isSidebarOpen)}
				className="md:hidden flex mt-3 ml-3"
				aria-label={
					isSidebarOpen
						? m["settings.close_sidebar_button"]()
						: m["settings.open_sidebar_button"]()
				}
			>
				{isSidebarOpen ? (
					<ChevronLeft className="size-4" />
				) : (
					<Menu className="size-4" />
				)}
			</Button>

			{/* Sidebar */}
			<div
				className={cn(
					"fixed top-16 bottom-0 left-0 z-40 transition-transform transform duration-300 ease-out",
					"bg-white backdrop-blur-sm md:backdrop-blur-none w-72 border-r p-4",
					{
						"translate-x-0": isSidebarOpen,
						"-translate-x-full md:translate-x-0": !isSidebarOpen,
					},
				)}
			>
				<h2 className="text-lg font-semibold mb-4 text-pink-700">
					{m["settings.title"]()}
				</h2>
				<ul className="flex flex-col gap-1">
					<li>
						<SettingsNavLink
							href="/app/settings/profile"
							icon={UserIcon}
							label={m["settings.profile_link"]()}
							onClick={() => setIsSidebarOpen(false)}
						/>
					</li>
					<li>
						<SettingsNavLink
							href="/app/settings/parameters"
							icon={Settings2Icon}
							label={m["settings.srs_parameters_link"]()}
							onClick={() => setIsSidebarOpen(false)}
						/>
					</li>
					<li>
						<SettingsNavLink
							href="/app/settings/ai-preferences"
							icon={BotMessageSquareIcon}
							label={m["settings.ai_preferences_link"]()}
							onClick={() => setIsSidebarOpen(false)}
						/>
					</li>
				</ul>
			</div>

			{/* Dark overlay when sidebar is open on mobile */}
			<div
				className={cn(
					"fixed inset-0 bg-black/20 z-30 md:hidden",
					"transition-opacity duration-300 ease-out",
					{
						"opacity-100 pointer-events-auto": isSidebarOpen,
						"opacity-0 pointer-events-none": !isSidebarOpen,
					},
				)}
				onClick={() => setIsSidebarOpen(false)}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						setIsSidebarOpen(false);
					}
				}}
				role="button"
				tabIndex={0}
			/>

			<div className="flex-1 overflow-y-auto p-4 md:p-8">
				<div className="max-w-screen-md mx-auto">
					<Outlet />
				</div>
			</div>
		</div>
	);
}
